// Phase 138.B / 138.C — `/try` upload-first free funnel.
//
// Structure:
//   1. AuthGate — shown when the visitor is not signed in. Explains
//      the one-free-lesson model and exposes a single Google sign-in
//      CTA. The upload form is hidden behind this.
//   2. UploadForm — signed-in form: drag-drop zone + grade picker +
//      Generate button. Progress bar with rotating status messages
//      while Gemini reads the file and Claude builds the lesson.
//   3. ResultView — Lesson Plan section (with copy button), three
//      worksheet tabs (Striving / On-grade / Extended, each with its
//      own copy button), Exit Ticket section (with copy button),
//      Start-trial CTA.
//   4. UsedState — soft paywall when the teacher has already burned
//      their one-time free lesson.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  FileText,
  Loader2,
  LogIn,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import {
  getTryStatus,
  trackTryTrialClick,
  uploadAndGenerateTry,
} from "../lib/tryApi";
import { track } from "../lib/analytics";
import { markdownComponents } from "./LessonDocument";

const GRADES = ["Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];
const ACCEPTED_EXTS = [".pdf", ".docx", ".doc", ".txt"];
const ACCEPTED_MIMES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/plain",
];
const MAX_BYTES = 10 * 1024 * 1024;

// Rotating status copy — surfaced during the "thinking" phase so the
// teacher sees forward motion instead of a single static spinner.
const THINKING_STATUSES = [
  "Reading your resource…",
  "Identifying key concepts and big ideas…",
  "Mapping to the Ontario curriculum…",
  "Sketching the lesson plan…",
  "Building differentiated worksheets…",
  "Drafting the exit ticket…",
  "Polishing the final lesson…",
];

const FIELD =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md h-11";

// ----------------------------------------------------------------- //
// Helpers                                                            //
// ----------------------------------------------------------------- //

const isAcceptedFile = (file) => {
  if (!file) return false;
  const name = (file.name || "").toLowerCase();
  if (ACCEPTED_EXTS.some((ext) => name.endsWith(ext))) return true;
  return ACCEPTED_MIMES.includes(file.type || "");
};

const prettyBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Markdown → plain text for copy-to-clipboard. Mirrors the strip
// rules in `lib/copyLesson.js` so paste output is consistent across
// the app.
const stripMd = (md) => {
  if (!md) return "";
  return String(md)
    .replace(/```[\s\S]*?```/g, (block) =>
      block.replace(/```\w*\n?/g, "").replace(/```$/g, "")
    )
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/^\s*[-*+]\s+/gm, "• ")
    .replace(/^-{3,}\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const copyText = async (text) => {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
};

// ----------------------------------------------------------------- //
// Top-level component                                                //
// ----------------------------------------------------------------- //

export default function TryUpload() {
  const { user, loading: authLoading, signIn } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [grade, setGrade] = useState("");
  const [dragOver, setDragOver] = useState(false);

  // status: 'unknown' | 'available' | 'used'
  const [status, setStatus] = useState("unknown");
  const [statusLoaded, setStatusLoaded] = useState(false);

  // submission: 'idle' | 'uploading' | 'thinking' | 'done' | 'error'
  const [submission, setSubmission] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Initial status fetch + refresh after sign-in.
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getTryStatus();
        if (cancelled) return;
        setStatus(data?.has_used ? "used" : "available");
      } catch {
        if (cancelled) return;
        setStatus("available");
      } finally {
        if (!cancelled) setStatusLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.email]);

  // File handlers ---------------------------------------------------
  const onFileChosen = useCallback((picked) => {
    if (!picked) return;
    if (!isAcceptedFile(picked)) {
      toast.error("Unsupported file type", {
        description: "Upload a PDF, Word doc, or plain-text file.",
      });
      return;
    }
    if (picked.size > MAX_BYTES) {
      toast.error("That file is too large", {
        description: "Maximum size is 10 MB.",
      });
      return;
    }
    setFile(picked);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      onFileChosen(e.dataTransfer?.files?.[0]);
    },
    [onFileChosen]
  );

  const onPickClick = () => fileInputRef.current?.click();
  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Generate --------------------------------------------------------
  const canSubmit = !!file && !!grade && submission === "idle";

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    if (!file || !grade) {
      toast.error("Pick a file and a grade first.");
      return;
    }
    setSubmission("uploading");
    setUploadProgress(0);
    setErrorMessage("");
    track("try_upload_generate_clicked", { grade });

    try {
      const data = await uploadAndGenerateTry(file, grade, (pct) => {
        setUploadProgress(pct);
        if (pct >= 100) setSubmission("thinking");
      });
      setResult(data);
      setSubmission("done");
      setStatus("used");
      track("try_upload_generate_succeeded", {
        grade,
        lead_id: data?.lead_id || "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong building the lesson.";
      const statusCode = err?.response?.status;
      if (statusCode === 409) {
        setStatus("used");
        setSubmission("idle");
        toast.error("You've already used your free try.", {
          description: "Start a 7-day trial to keep building.",
        });
      } else if (statusCode === 401) {
        signIn();
      } else {
        setErrorMessage(String(detail));
        setSubmission("error");
        toast.error("Couldn't build the lesson", { description: String(detail) });
      }
    }
  };

  // Trial click ------------------------------------------------------
  const handleTrialClick = async () => {
    if (result?.lead_id) {
      trackTryTrialClick(result.lead_id);
      track("try_upload_trial_clicked", { lead_id: result.lead_id });
    }
    navigate("/pricing");
  };

  // Resets the form to idle so the teacher can run another generation
  // (subject to one-time-use gate).
  const handleReset = () => {
    setResult(null);
    setSubmission("idle");
    setFile(null);
    setGrade("");
  };

  // -----------------------------------------------------------------
  // Render branches
  // -----------------------------------------------------------------

  // Auth gate — signed-out teachers never see the form.
  if (!authLoading && !user) {
    return <AuthGate onSignIn={signIn} />;
  }

  // Already-used soft paywall (only after we've confirmed status).
  if (statusLoaded && status === "used" && submission !== "done") {
    return <UsedState />;
  }

  // Generation finished — show the artifacts.
  if (submission === "done" && result) {
    return (
      <ResultView
        result={result}
        onStartTrial={handleTrialClick}
        onReset={handleReset}
      />
    );
  }

  // Default — upload form (signed-in).
  return (
    <UploadForm
      user={user}
      file={file}
      grade={grade}
      dragOver={dragOver}
      submission={submission}
      uploadProgress={uploadProgress}
      errorMessage={errorMessage}
      canSubmit={canSubmit}
      fileInputRef={fileInputRef}
      onFileChosen={onFileChosen}
      onDrop={onDrop}
      setDragOver={setDragOver}
      setGrade={setGrade}
      onPickClick={onPickClick}
      onClearFile={clearFile}
      onSubmit={handleGenerate}
    />
  );
}

// ----------------------------------------------------------------- //
// AuthGate — explicit Google sign-in panel                           //
// ----------------------------------------------------------------- //

const AuthGate = ({ onSignIn }) => (
  <section
    data-testid="try-auth-gate"
    className="rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden"
  >
    <div className="bg-[#1c2d3d] text-white px-6 py-5 sm:px-8 sm:py-6">
      <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
        Sign in to try
      </p>
      <h2 className="mt-1 font-black text-2xl sm:text-3xl tracking-tight leading-tight">
        One free lesson, on us.
        <br />
        No credit card. No setup.
      </h2>
      <p className="mt-2 text-sm sm:text-base text-white/75 font-medium leading-relaxed">
        Atomic Edu uses Google sign-in only to make sure every teacher
        gets one free lesson — and to email yours back to you.
      </p>
    </div>
    <div className="px-6 py-6 sm:px-8 sm:py-8 space-y-5">
      <ul className="space-y-2 text-sm text-[#4a6984] leading-relaxed">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
          <span>Upload a PDF, Word doc, or text file (up to 10MB).</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
          <span>Get a full Ontario-aligned lesson plan + 3 differentiated worksheets + exit ticket.</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
          <span>Copy or print anything you want. No strings attached.</span>
        </li>
      </ul>
      <Button
        onClick={onSignIn}
        data-testid="try-auth-gate-signin"
        className="ae-cta hover:ae-cta h-12 w-full sm:w-auto px-6 rounded-md font-extrabold inline-flex items-center justify-center gap-2"
      >
        <LogIn className="h-4 w-4" />
        Sign in with Google to start
        <ArrowRight className="h-4 w-4" />
      </Button>
      <p className="text-xs text-[#4a6984]/65">
        Already have an account?{" "}
        <Link
          to="/pricing"
          data-testid="try-auth-gate-plans-link"
          className="font-bold text-[#1c2d3d] hover:underline"
        >
          See plans →
        </Link>
      </p>
    </div>
  </section>
);

// ----------------------------------------------------------------- //
// UploadForm                                                         //
// ----------------------------------------------------------------- //

const UploadForm = ({
  user,
  file,
  grade,
  dragOver,
  submission,
  uploadProgress,
  errorMessage,
  canSubmit,
  fileInputRef,
  onFileChosen,
  onDrop,
  setDragOver,
  setGrade,
  onPickClick,
  onClearFile,
  onSubmit,
}) => (
  <section
    data-testid="try-upload-form"
    className="rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden"
  >
    <div className="bg-[#1c2d3d] text-white px-6 py-4 sm:px-8 sm:py-5">
      <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
        Upload a classroom resource
      </p>
      <h2
        data-testid="try-upload-heading"
        className="mt-1 font-black text-2xl sm:text-3xl tracking-tight leading-tight"
      >
        Drop a file. We&apos;ll build a full lesson around it.
      </h2>
      <p className="mt-2 text-sm sm:text-base text-white/75 font-medium leading-relaxed">
        Signed in as{" "}
        <span className="font-extrabold text-white">{user?.email}</span>.
        One free lesson on us.
      </p>
    </div>

    <form onSubmit={onSubmit} className="px-6 py-6 sm:px-8 sm:py-8 space-y-6">
      {/* File drop zone */}
      <div className="space-y-2">
        <Label className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80">
          1. Your resource
        </Label>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTS.join(",")}
          className="hidden"
          data-testid="try-file-input"
          onChange={(e) => onFileChosen(e.target.files?.[0])}
        />
        {file ? (
          <div
            data-testid="try-file-chip"
            className="flex items-center gap-3 rounded-lg border border-[#96ded1] bg-[#96ded1]/15 px-4 py-3"
          >
            <FileText className="h-5 w-5 text-[#1c2d3d] shrink-0" />
            <div className="flex-1 min-w-0">
              <p
                data-testid="try-file-name"
                className="font-extrabold text-[#1c2d3d] text-sm truncate"
              >
                {file.name}
              </p>
              <p className="text-xs text-[#4a6984]/70 font-medium">
                {prettyBytes(file.size)}
              </p>
            </div>
            <button
              type="button"
              onClick={onClearFile}
              aria-label="Remove file"
              data-testid="try-file-clear"
              className="h-8 w-8 rounded-full grid place-items-center text-[#4a6984] hover:bg-[#4a6984]/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onPickClick}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="try-file-dropzone"
            className={`w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 sm:py-12 transition-colors text-center ${
              dragOver
                ? "border-[#96ded1] bg-[#96ded1]/10"
                : "border-[#4a6984]/25 bg-[#f4f5f7] hover:border-[#96ded1] hover:bg-[#96ded1]/5"
            }`}
          >
            <Upload className="h-6 w-6 text-[#4a6984]" />
            <p className="font-extrabold text-[#1c2d3d] text-sm sm:text-base">
              Drag a file here, or click to browse
            </p>
            <p className="text-xs text-[#4a6984]/70 font-medium">
              PDF, Word, or TXT · 10 MB max
            </p>
          </button>
        )}
      </div>

      {/* Grade picker */}
      <div className="space-y-2">
        <Label className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80">
          2. Grade
        </Label>
        <Select value={grade} onValueChange={setGrade}>
          <SelectTrigger data-testid="try-grade" className={FIELD}>
            <SelectValue placeholder="Pick a grade" />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
            {GRADES.map((g) => (
              <SelectItem
                key={g}
                value={g}
                data-testid={`try-grade-${g.replace(/\s/g, "-").toLowerCase()}`}
                className="text-[#4a6984] focus:bg-[#f4f5f7]"
              >
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Progress + error states */}
      {submission === "uploading" && (
        <ProgressBar label="Uploading your file…" pct={uploadProgress} />
      )}
      {submission === "thinking" && <ThinkingProgress />}
      {submission === "error" && errorMessage && (
        <p
          data-testid="try-upload-error"
          className="text-sm text-red-600 font-medium"
        >
          {errorMessage}
        </p>
      )}

      {/* Submit */}
      <div className="pt-1">
        <Button
          type="submit"
          data-testid="try-upload-submit"
          disabled={!canSubmit}
          className="ae-cta hover:ae-cta h-12 px-6 rounded-md font-extrabold w-full sm:w-auto inline-flex items-center gap-2 disabled:opacity-50"
        >
          {submission === "uploading" || submission === "thinking" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {submission === "uploading" ? "Uploading…" : "Generating…"}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Generate my free lesson
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  </section>
);

// ----------------------------------------------------------------- //
// Progress UI                                                        //
// ----------------------------------------------------------------- //

const ProgressBar = ({ label, pct }) => (
  <div
    data-testid="try-upload-progress"
    className="rounded-lg border border-[#4a6984]/15 bg-[#f4f5f7] px-4 py-3 space-y-2"
  >
    <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/80">
      {label}
    </p>
    <div className="h-2 rounded-full bg-[#4a6984]/10 overflow-hidden">
      <div
        className="h-full bg-[#96ded1] transition-[width] duration-300"
        style={{ width: `${Math.max(8, pct)}%` }}
      />
    </div>
  </div>
);

// Rotating status messages while the LLM does its work. Lives as its
// own component so the rotation timer is mounted/unmounted with the
// thinking phase only.
const ThinkingProgress = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % THINKING_STATUSES.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      data-testid="try-upload-progress"
      className="rounded-lg border border-[#4a6984]/15 bg-[#f4f5f7] px-4 py-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 text-[#1c2d3d] animate-spin" />
        <p
          data-testid="try-thinking-status"
          className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/85 transition-opacity"
          key={idx}
        >
          {THINKING_STATUSES[idx]}
        </p>
      </div>
      <div className="h-2 rounded-full bg-[#4a6984]/10 overflow-hidden">
        <div className="h-full w-full bg-[#96ded1] animate-pulse" />
      </div>
      <p className="text-[11px] text-[#4a6984]/65 font-medium italic">
        This usually takes a minute or two.
      </p>
    </div>
  );
};

// ----------------------------------------------------------------- //
// UsedState                                                          //
// ----------------------------------------------------------------- //

const UsedState = () => (
  <section
    data-testid="try-used-state"
    className="rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden"
  >
    <div className="bg-[#1c2d3d] text-white px-6 py-5 sm:px-8 sm:py-6">
      <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
        Welcome back
      </p>
      <h2 className="mt-1 font-black text-2xl sm:text-3xl tracking-tight">
        You&apos;ve already claimed your free lesson.
      </h2>
    </div>
    <div className="px-6 py-6 sm:px-8 sm:py-8 space-y-5">
      <p className="text-[#4a6984] leading-relaxed">
        Ready to keep building? Start your 7-day free trial — no credit
        card needed.
      </p>
      <Button
        asChild
        data-testid="try-used-see-plans"
        className="ae-cta hover:ae-cta h-12 w-full sm:w-auto rounded-md font-extrabold inline-flex items-center justify-center gap-2"
      >
        <Link to="/pricing">
          See plans <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    </div>
  </section>
);

// ----------------------------------------------------------------- //
// ResultView — Lesson Plan + 3-tab worksheets + Exit Ticket          //
// ----------------------------------------------------------------- //

const WORKSHEET_TABS = [
  { key: "worksheet_striving_markdown", label: "Striving", testid: "tab-striving" },
  { key: "worksheet_on_grade_markdown", label: "On grade", testid: "tab-on-grade" },
  { key: "worksheet_extended_markdown", label: "Extended", testid: "tab-extended" },
];

const CopyButton = ({ markdown, label, testid }) => {
  const [copied, setCopied] = useState(false);
  const handleClick = async () => {
    const ok = await copyText(stripMd(markdown));
    if (ok) {
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Couldn't copy — try selecting the text manually.");
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-md border border-[#4a6984]/20 bg-white px-3 py-1.5 text-xs font-bold text-[#4a6984] hover:bg-[#f4f5f7] hover:text-[#1c2d3d] transition-colors"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-[#96ded1]" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy {label}
        </>
      )}
    </button>
  );
};

const ArtifactSection = ({ title, eyebrow, markdown, copyLabel, copyTestid, sectionTestid, children }) => (
  <section
    data-testid={sectionTestid}
    className="rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden"
  >
    <header className="flex items-start justify-between gap-4 border-b border-[#4a6984]/10 px-5 py-4 sm:px-7">
      <div className="min-w-0">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
          {eyebrow}
        </p>
        <h3 className="mt-1 font-extrabold text-xl sm:text-2xl text-[#1c2d3d] tracking-tight">
          {title}
        </h3>
      </div>
      {markdown ? (
        <CopyButton
          markdown={markdown}
          label={copyLabel}
          testid={copyTestid}
        />
      ) : null}
    </header>
    <div className="px-5 sm:px-7 py-6">{children}</div>
  </section>
);

const MarkdownBody = ({ markdown, testid }) => (
  <article data-testid={testid} className="prose prose-slate max-w-none">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {markdown || ""}
    </ReactMarkdown>
  </article>
);

const ResultView = ({ result, onStartTrial, onReset }) => {
  const filename = result?.source_file_name || "your resource";
  const grade = result?.grade || "";
  const worksheetTabs = useMemo(
    () => WORKSHEET_TABS.filter((t) => (result?.[t.key] || "").trim().length > 0),
    [result]
  );
  const defaultWorksheetTab = worksheetTabs[0]?.key || WORKSHEET_TABS[0].key;
  const [activeWorksheetTab, setActiveWorksheetTab] = useState(defaultWorksheetTab);
  const activeWorksheetMd = result?.[activeWorksheetTab] || "";

  return (
    <div data-testid="try-result" className="space-y-6">
      {/* Header card */}
      <section className="rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden">
        <div className="bg-[#1c2d3d] text-white px-6 py-5 sm:px-8 sm:py-6 flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-[#96ded1] mt-1 shrink-0" />
          <div className="flex-1">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
              Your free lesson is ready
            </p>
            <h2
              data-testid="try-result-heading"
              className="mt-1 font-black text-2xl sm:text-3xl tracking-tight leading-tight"
            >
              Built around {filename}
              {grade ? ` · ${grade}` : ""}
            </h2>
          </div>
        </div>
      </section>

      {/* Lesson plan */}
      {(result?.lesson_plan_markdown || "").trim() && (
        <ArtifactSection
          eyebrow="Lesson Plan"
          title="Three-part Ontario lesson"
          markdown={result.lesson_plan_markdown}
          copyLabel="lesson plan"
          copyTestid="try-copy-lesson-plan"
          sectionTestid="try-lesson-plan-section"
        >
          <MarkdownBody
            markdown={result.lesson_plan_markdown}
            testid="try-lesson-plan-body"
          />
        </ArtifactSection>
      )}

      {/* Worksheets — 3-tab */}
      {worksheetTabs.length > 0 && (
        <ArtifactSection
          eyebrow="Worksheets"
          title="Three-level differentiated worksheets"
          markdown={activeWorksheetMd}
          copyLabel="worksheet"
          copyTestid="try-copy-worksheet"
          sectionTestid="try-worksheets-section"
        >
          <Tabs
            value={activeWorksheetTab}
            onValueChange={setActiveWorksheetTab}
            className="w-full"
          >
            <TabsList
              data-testid="try-worksheet-tabs"
              className="w-full justify-start overflow-x-auto bg-[#f4f5f7] p-1 rounded-md"
            >
              {worksheetTabs.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  data-testid={`try-${t.testid}`}
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2d3d] text-[#4a6984] font-bold text-sm px-4"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {worksheetTabs.map((t) => (
              <TabsContent
                key={t.key}
                value={t.key}
                data-testid={`try-${t.testid}-content`}
                className="pt-6"
              >
                <MarkdownBody
                  markdown={result[t.key]}
                  testid={`try-${t.testid}-body`}
                />
              </TabsContent>
            ))}
          </Tabs>
        </ArtifactSection>
      )}

      {/* Exit ticket */}
      {(result?.exit_ticket_markdown || "").trim() && (
        <ArtifactSection
          eyebrow="Exit Ticket"
          title="Three-question check for understanding"
          markdown={result.exit_ticket_markdown}
          copyLabel="exit ticket"
          copyTestid="try-copy-exit-ticket"
          sectionTestid="try-exit-ticket-section"
        >
          <MarkdownBody
            markdown={result.exit_ticket_markdown}
            testid="try-exit-ticket-body"
          />
        </ArtifactSection>
      )}

      {/* Trial CTA */}
      <section className="rounded-2xl border border-[#96ded1] bg-[#96ded1]/15 px-5 py-5 sm:px-7 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#1c2d3d]">
            Keep building
          </p>
          <p className="mt-1 font-black text-lg sm:text-xl text-[#1c2d3d] leading-snug">
            Get the full Atomic Edu generator — units, summative tasks,
            and unlimited lessons.
          </p>
        </div>
        <Button
          onClick={onStartTrial}
          data-testid="try-start-trial"
          className="ae-cta hover:ae-cta h-12 px-6 rounded-md font-extrabold inline-flex items-center gap-2 shrink-0"
        >
          Start 7-day free trial
          <ArrowRight className="h-4 w-4" />
        </Button>
      </section>

      <div className="text-center">
        <button
          type="button"
          onClick={onReset}
          data-testid="try-result-reset"
          className="text-xs font-bold text-[#4a6984]/70 hover:text-[#1c2d3d] underline"
        >
          Start over
        </button>
      </div>
    </div>
  );
};
